import type { SnapError, SnapFailureKind } from '../types';
import { SnapDenyCodes } from '../types';

const SENSITIVE_PATTERNS = [
  /authorization/i,
  /bearer\s+[a-z0-9\-._~+/]+=*/i,
  /appkey/i,
  /appsecret/i,
  /signingkey/i
];

export function createSnapError(
  kind: SnapFailureKind,
  code: string,
  message: string,
  opts?: { correlationId?: string; retryable?: boolean; userSafe?: boolean }
): SnapError {
  return {
    kind,
    code,
    message: sanitizeLogText(message),
    correlationId: opts?.correlationId,
    retryable: opts?.retryable ?? false,
    userSafe: opts?.userSafe ?? true
  };
}

export function sanitizeLogText(value: string): string {
  let out = String(value ?? '');
  for (const pattern of SENSITIVE_PATTERNS) {
    out = out.replace(pattern, '[redacted]');
  }
  return out.slice(0, 500);
}

/** Escape text for safe textContent-equivalent HTML attribute usage. */
export function escapeHtml(value: string): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function safeText(el: Element, text: string): void {
  el.textContent = String(text ?? '');
}

export function isPlainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

export function assertNoEvalInSource(source: string): boolean {
  return !/\beval\s*\(|new\s+Function\s*\(/.test(source);
}

export const DefaultDeny = {
  expired: (correlationId?: string) =>
    createSnapError(
      'bootstrap_expired',
      SnapDenyCodes.BootstrapExpired,
      'Snap bootstrap authorization has expired.',
      { correlationId, retryable: true, userSafe: true }
    ),
  missingAuth: (correlationId?: string) =>
    createSnapError(
      'authorization_missing',
      SnapDenyCodes.BootstrapInvalid,
      'Snap bootstrap authorization is missing.',
      { correlationId, userSafe: true }
    ),
  appKey: () =>
    createSnapError(
      'appkey_rejected',
      SnapDenyCodes.AppKeyRejected,
      'AppKey is not accepted as browser configuration.',
      { userSafe: true }
    )
};
